import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useContractGameStore } from '../../store/useContractGameStore'
import { previewHandScore } from '../../engine/pokerEvaluator'
import { HAND_TYPE_CONFIG, HAND_TYPE_ORDER } from '../../data/pokerConfig'
import type { PokerHandType } from '../../engine/types'

type PokerSidebarProps = {
  header: React.ReactNode
  animationChips?: number
  animationMult?: number
  isAnimating?: boolean
  animationHandType?: string
  animationHandLevel?: number
  animationPhase?: string
}

export function PokerSidebar({
  header,
  animationChips,
  animationMult,
  isAnimating = false,
  animationHandType,
  animationHandLevel,
  animationPhase,
}: PokerSidebarProps) {
  const { t } = useTranslation()

  const shopMoney = useContractGameStore((s) => s.shopMoney)
  const runningScore = useContractGameStore((s) => s.runningScore)
  const clauseThresholds = useContractGameStore((s) => s.clauseThresholds)
  const selectedCardIds = useContractGameStore((s) => s.selectedCardIds)
  const hand = useContractGameStore((s) => s.hand)
  const handTypeLevels = useContractGameStore((s) => s.handTypeLevels)
  const handsRemaining = useContractGameStore((s) => s.handsRemaining)
  const discardsRemaining = useContractGameStore((s) => s.discardsRemaining)
  const clausePool = useContractGameStore((s) => s.clausePool)
  const allClauseThresholds = useContractGameStore((s) => s.allClauseThresholds)
  const currentBlind = useContractGameStore((s) => s.currentBlind)
  const phase = useContractGameStore((s) => s.phase)

  const selectedCards = useMemo(() => {
    const handById = new Map(hand.map((card) => [card.id, card]))
    return selectedCardIds
      .map((cardId) => handById.get(cardId))
      .filter((card): card is (typeof hand)[number] => card !== undefined)
  }, [hand, selectedCardIds])

  const nextThreshold = clauseThresholds.find((th) => !th.reversed) ?? null
  const reversedThresholdCount = clauseThresholds.filter((th) => th.reversed).length

  const preview = useMemo(
    () => previewHandScore(selectedCards, handTypeLevels),
    [selectedCards, handTypeLevels],
  )

  // Remember last valid preview so we don't flash 0×0 during the gap
  // between play-hand dispatch (clears selection) and animation start
  const lastPreviewRef = useRef(preview)
  if (preview) lastPreviewRef.current = preview

  // Unified display values: animation overrides preview
  const showAnim = isAnimating && animationChips !== undefined && animationMult !== undefined && animationPhase !== 'cards_spread'
  const fallback = preview ?? lastPreviewRef.current
  const displayHandType = showAnim ? (animationHandType ?? null) : (fallback?.handTypeCN ?? null)
  const displayHandLevel = showAnim ? (animationHandLevel ?? 1) : (fallback?.level ?? null)
  const displayChips = showAnim ? Math.round(animationChips!) : (fallback ? Math.round(fallback.chips) : 0)
  const displayMult = showAnim ? Math.round(animationMult! * 10) / 10 : (fallback ? Math.round(fallback.mult * 10) / 10 : 0)

  return (
    <div
      className="w-[260px] shrink-0 flex flex-col overflow-y-auto"
      style={{
        background: 'linear-gradient(180deg, #1A2F25 0%, #152820 100%)',
        border: '2px solid rgba(201,169,110,0.5)',
        boxShadow: '4px 0 16px rgba(0,0,0,0.3)',
      }}
    >
      {/* Header slot — fixed height container for consistent layout */}
      <div className="shrink-0 w-full flex flex-col justify-center" style={{ minHeight: 88 }}>
        {header}
      </div>

      {/* Score Target */}
      <div className="mx-3 mb-2">
        <PanelHeader label={t('sidebar.targetLabel')} />
        <div
          className="rounded-b-lg p-3"
          style={{
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderTop: 'none',
          }}
        >
          {nextThreshold ? (
            <>
              <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'rgba(201,169,110,0.5)' }}>
                {t('sidebar.reach')}
              </div>
              <div
                className="text-2xl font-black font-mono text-center"
                style={{ color: '#E8D060', textShadow: '0 0 12px rgba(232,208,96,0.3)' }}
              >
                {nextThreshold.threshold.toLocaleString()}
              </div>
              <div className="text-[9px] text-center mt-1" style={{ color: 'rgba(180,220,190,0.4)' }}>
                {nextThreshold.clauseId} · {t('sidebar.broken', { reversed: reversedThresholdCount, total: clauseThresholds.length })}
              </div>
              {/* Mini progress bar */}
              <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.3)' }}>
                <div
                  className="h-full rounded-full transition-[width] duration-500"
                  style={{
                    width: `${Math.min(100, (runningScore / nextThreshold.threshold) * 100)}%`,
                    background: 'linear-gradient(90deg, #7CAFC2, #7DAF8B)',
                  }}
                />
              </div>
            </>
          ) : (
            <div className="text-center text-sm font-bold" style={{ color: '#7DAF8B' }}>
              {t('sidebar.allBroken')}
            </div>
          )}
        </div>
      </div>

      {/* Running Score */}
      <div className="mx-3 mb-2" data-score-panel>
        <PanelHeader label={t('sidebar.runningScore')} />
        <div
          className="rounded-b-lg p-3 text-center"
          style={{
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderTop: 'none',
          }}
        >
          <div
            className="text-3xl font-black font-mono"
            style={{ color: '#C9A96E', textShadow: '0 0 10px rgba(201,169,110,0.25)' }}
          >
            {runningScore.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Hand Type Preview / Animation Chips×Mult */}
      <div className="mx-3 mb-2">
        <PanelHeader label={isAnimating ? t('sidebar.scoring', 'Scoring') : t('sidebar.handPreview')} />
        <div
          className="rounded-b-lg p-3 flex flex-col justify-center"
          style={{
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderTop: 'none',
            minHeight: 80,
          }}
        >
          {/* Hand type name */}
          <div className="text-center mb-1.5">
            <span className="text-sm font-bold" style={{ color: displayHandType ? '#C9A96E' : 'transparent' }}>
              {displayHandType ?? '\u00A0'}
            </span>
            {displayHandLevel != null && (
              <span className="text-[10px] ml-1" style={{ color: 'rgba(201,169,110,0.5)' }}>
                {isAnimating ? 'Lv.' : 'lvl.'}{displayHandLevel}
              </span>
            )}
          </div>
          {/* Chips × Mult */}
          <div className="flex items-center justify-center gap-2">
            <span
              className="px-3 py-1 rounded text-xl font-black font-mono"
              style={{ background: 'rgba(124,175,194,0.2)', color: '#7CAFC2' }}
              key={isAnimating ? `anim-chips-${displayChips}` : 'chips'}
            >
              {displayChips}
            </span>
            <span className="text-sm font-bold" style={{ color: 'rgba(255,255,255,0.3)' }}>
              ×
            </span>
            <span
              className="px-3 py-1 rounded text-xl font-black font-mono"
              style={{ background: 'rgba(212,132,154,0.2)', color: '#D4849A' }}
              key={isAnimating ? `anim-mult-${displayMult}` : 'mult'}
            >
              {displayMult}
            </span>
          </div>
        </div>
      </div>

      {/* Hands & Discards */}
      <div className="mx-3 mb-2">
        <div
          className="rounded-lg p-3 flex items-center justify-around"
          style={{
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div className="text-center">
            <div className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: 'rgba(124,175,194,0.5)' }}>
              {t('sidebar.hands')}
            </div>
            <div className="text-xl font-black font-mono" style={{ color: '#7CAFC2' }}>
              {handsRemaining}
            </div>
          </div>
          <div className="w-px h-6" style={{ background: 'rgba(255,255,255,0.08)' }} />
          <div className="text-center">
            <div className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: 'rgba(212,132,154,0.5)' }}>
              {t('sidebar.discards')}
            </div>
            <div className="text-xl font-black font-mono" style={{ color: '#D4849A' }}>
              {discardsRemaining}
            </div>
          </div>
        </div>
      </div>

      {/* Money */}
      <div className="mx-3 mb-2">
        <div
          className="rounded-lg p-3 flex items-center justify-between"
          style={{
            background: 'rgba(0,0,0,0.25)',
            border: '1px solid rgba(201,169,110,0.2)',
          }}
        >
          <div className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(201,169,110,0.6)' }}>
            {t('shop.money')}
          </div>
          <div
            className="text-2xl font-black"
            style={{
              color: '#E8D060',
              textShadow: '0 0 12px rgba(232,208,96,0.3)',
            }}
          >
            ${shopMoney}
          </div>
        </div>
      </div>

      {/* Clause Milestones (vertical) — hidden in shop */}
      {phase !== 'PACK_SHOP' && (
        <ClauseMilestonePanel
          thresholds={clauseThresholds}
          clausePool={clausePool}
          runningScore={runningScore}
        />
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Run Info — clickable button that opens modal */}
      <RunInfoButton
        handTypeLevels={handTypeLevels}
        clauseThresholds={allClauseThresholds}
        clausePool={clausePool}
        runningScore={runningScore}
        currentBlindClauseIds={currentBlind?.clauseIds ?? []}
      />
    </div>
  )
}

// =============================================================================
// ClauseMilestonePanel — vertical clause cards in sidebar
// =============================================================================

function ClauseMilestonePanel({
  thresholds,
  clausePool,
  runningScore,
}: {
  thresholds: import('../../engine/types').ClauseThreshold[]
  clausePool: import('../../engine/types').ClauseLine[]
  runningScore: number
}) {
  if (thresholds.length === 0) return null

  return (
    <div className="mx-3 mb-2 flex flex-col gap-1.5">
      {thresholds.map((th) => {
        const clause = clausePool.find((c) => c.id === th.clauseId)
        if (!clause) return null

        const isReversed = th.reversed
        const isNext =
          !isReversed &&
          thresholds.every(
            (other) => other.clauseId === th.clauseId || other.reversed || other.threshold >= th.threshold,
          )
        const progress = isReversed ? 100 : Math.min(100, (runningScore / th.threshold) * 100)

        return (
          <div
            key={th.clauseId}
            className="w-full rounded-md overflow-hidden transition-all duration-300"
            style={{
              background: isReversed
                ? 'rgba(125,175,139,0.15)'
                : isNext
                  ? 'rgba(201,169,110,0.12)'
                  : 'rgba(0,0,0,0.2)',
              border: isReversed
                ? '1px solid rgba(125,175,139,0.3)'
                : isNext
                  ? '1px solid rgba(201,169,110,0.3)'
                  : '1px solid rgba(255,255,255,0.06)',
            }}
          >
            {/* Header: ID + threshold */}
            <div className="flex items-center justify-between px-2 pt-1.5">
              <span
                className="text-[9px] font-mono font-bold flex items-center gap-1"
                style={{ color: isReversed ? '#7DAF8B' : 'rgba(180,220,190,0.5)' }}
              >
                {th.clauseId}
                {clause.isBoss && (
                  <span
                    className="px-1 rounded-[2px] text-[7px] font-bold"
                    style={{ background: 'rgba(212,132,154,0.3)', color: '#D4849A' }}
                  >
                    BOSS
                  </span>
                )}
              </span>
              <span
                className="text-[9px] font-mono"
                style={{ color: isReversed ? '#7DAF8B' : 'rgba(180,220,190,0.35)' }}
              >
                {th.threshold.toLocaleString()}
              </span>
            </div>

            {/* Clause short text */}
            <div
              className="px-2 text-[10px] leading-tight truncate"
              style={{ color: isReversed ? 'rgba(125,175,139,0.7)' : 'rgba(230,220,200,0.6)' }}
            >
              {clause.shortText}
            </div>

            {/* Progress bar */}
            <div className="mx-2 mt-1 mb-1.5 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.3)' }}>
              <div
                className="h-full rounded-full transition-[width] duration-500"
                style={{
                  width: `${progress}%`,
                  background: isReversed ? '#7DAF8B' : 'linear-gradient(90deg, #7CAFC2, #C9A96E)',
                }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// =============================================================================
// RunInfoButton + RunInfoModal — clickable button opens hand type info modal
// =============================================================================

function RunInfoButton({
  handTypeLevels,
  clauseThresholds,
  clausePool,
  runningScore,
  currentBlindClauseIds,
}: {
  handTypeLevels: Record<PokerHandType, import('../../engine/types').HandTypeLevel>
  clauseThresholds: import('../../engine/types').ClauseThreshold[]
  clausePool: import('../../engine/types').ClauseLine[]
  runningScore: number
  currentBlindClauseIds: string[]
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <>
      <div className="mx-3 mb-3">
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          className="w-full rounded-lg px-3 py-2.5 text-[11px] font-bold uppercase tracking-[2px] text-center cursor-pointer transition-all duration-200"
          style={{
            background: 'linear-gradient(135deg, #4A3520, #5A4030, #4A3520)',
            color: '#C9A96E',
            border: '1px solid rgba(201,169,110,0.3)',
          }}
          data-run-info-trigger
          onClick={() => setOpen(true)}
        >
          {t('sidebar.runInfo', 'Run Info')}
        </button>
      </div>

      {open && (
        <RunInfoModal
          handTypeLevels={handTypeLevels}
          clauseThresholds={clauseThresholds}
          clausePool={clausePool}
          runningScore={runningScore}
          currentBlindClauseIds={currentBlindClauseIds}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

type RunInfoTab = 'handTypes' | 'clauses'

function RunInfoModal({
  handTypeLevels,
  clauseThresholds,
  clausePool,
  runningScore,
  currentBlindClauseIds,
  onClose,
}: {
  handTypeLevels: Record<PokerHandType, import('../../engine/types').HandTypeLevel>
  clauseThresholds: import('../../engine/types').ClauseThreshold[]
  clausePool: import('../../engine/types').ClauseLine[]
  runningScore: number
  currentBlindClauseIds: string[]
  onClose: () => void
}) {
  const { t } = useTranslation()
  const titleId = 'run-info-modal-title'
  const [activeTab, setActiveTab] = useState<RunInfoTab>('handTypes')

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleEscape)
    }
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ animation: 'fade-in 200ms ease-out' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative rounded-xl overflow-hidden"
        style={{
          width: 420,
          maxHeight: '80vh',
          background: 'linear-gradient(160deg, #1A2F25 0%, #152820 100%)',
          border: '2px solid rgba(201,169,110,0.5)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          animation: 'scale-in 250ms ease-out',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          className="px-5 py-3 flex items-center justify-between shrink-0"
          style={{
            background: 'linear-gradient(135deg, #4A3520, #5A4030, #4A3520)',
            borderBottom: '1px solid rgba(201,169,110,0.3)',
          }}
        >
          <div id={titleId} className="text-sm font-bold uppercase tracking-[2px]" style={{ color: '#C9A96E' }}>
            {t('sidebar.runInfo', 'Run Info')}
          </div>
          <button
            type="button"
            aria-label={t('shop.clickToClose')}
            className="text-[18px] leading-none cursor-pointer"
            style={{ color: 'rgba(201,169,110,0.5)', background: 'none', border: 'none' }}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {([
            { id: 'handTypes' as const, label: t('runInfo.tabHandTypes', 'Hand Types') },
            { id: 'clauses' as const, label: t('runInfo.tabClauses', 'Clauses') },
          ]).map(tab => (
            <button
              key={tab.id}
              type="button"
              className="flex-1 py-2 text-[11px] font-bold uppercase tracking-wider cursor-pointer transition-colors duration-150"
              style={{
                background: activeTab === tab.id ? 'rgba(201,169,110,0.1)' : 'transparent',
                color: activeTab === tab.id ? '#C9A96E' : 'rgba(180,220,190,0.35)',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #C9A96E' : '2px solid transparent',
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="overflow-y-auto flex-1">
          {activeTab === 'handTypes' && (
            <HandTypesTabContent handTypeLevels={handTypeLevels} />
          )}
          {activeTab === 'clauses' && (
            <ClausesTabContent
              clauseThresholds={clauseThresholds}
              clausePool={clausePool}
              runningScore={runningScore}
              currentBlindClauseIds={currentBlindClauseIds}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function HandTypesTabContent({
  handTypeLevels,
}: {
  handTypeLevels: Record<PokerHandType, import('../../engine/types').HandTypeLevel>
}) {
  const { t } = useTranslation()

  return (
    <>
      {/* Table header */}
      <div
        className="flex items-center px-5 py-2"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="flex-1 text-[9px] uppercase tracking-wider font-bold" style={{ color: 'rgba(180,220,190,0.35)' }}>
          {t('runInfo.handType', 'Hand Type')}
        </div>
        <div className="text-[9px] uppercase tracking-wider font-bold w-[48px] text-center" style={{ color: 'rgba(180,220,190,0.35)' }}>
          {t('runInfo.level', 'Level')}
        </div>
        <div className="text-[9px] uppercase tracking-wider font-bold w-[48px] text-right" style={{ color: 'rgba(124,175,194,0.5)' }}>
          Chips
        </div>
        <div className="text-[9px] w-[16px] text-center" style={{ color: 'rgba(255,255,255,0.15)' }}>×</div>
        <div className="text-[9px] uppercase tracking-wider font-bold w-[36px] text-right" style={{ color: 'rgba(212,132,154,0.5)' }}>
          Mult
        </div>
      </div>

      {/* Hand type rows */}
      <div className="px-2 py-1">
        {HAND_TYPE_ORDER.map((handType) => {
          const config = HAND_TYPE_CONFIG[handType]
          const level = handTypeLevels[handType]
          const isUpgraded = level.level > 1

          return (
            <div
              key={handType}
              className="flex items-center px-3 py-2 rounded-md mx-0 transition-colors duration-150"
              style={{
                background: isUpgraded ? 'rgba(201,169,110,0.08)' : 'transparent',
                borderBottom: '1px solid rgba(255,255,255,0.03)',
              }}
            >
              <div
                className="flex-1 text-[13px] font-bold"
                style={{ color: isUpgraded ? '#C9A96E' : 'rgba(230,220,200,0.7)' }}
              >
                {config.nameCN}
              </div>
              <div
                className="text-[11px] font-mono w-[48px] text-center"
                style={{ color: isUpgraded ? '#C9A96E' : 'rgba(180,220,190,0.4)' }}
              >
                Lv.{level.level}
              </div>
              <div
                className="text-[13px] font-mono font-bold w-[48px] text-right"
                style={{ color: '#7CAFC2' }}
              >
                {level.baseChips}
              </div>
              <div className="text-[11px] w-[16px] text-center" style={{ color: 'rgba(255,255,255,0.2)' }}>×</div>
              <div
                className="text-[13px] font-mono font-bold w-[36px] text-right"
                style={{ color: '#D4849A' }}
              >
                {level.baseMult}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

function ClausesTabContent({
  clauseThresholds,
  clausePool,
  runningScore,
  currentBlindClauseIds,
}: {
  clauseThresholds: import('../../engine/types').ClauseThreshold[]
  clausePool: import('../../engine/types').ClauseLine[]
  runningScore: number
  currentBlindClauseIds: string[]
}) {
  const { t } = useTranslation()
  const reversedCount = clauseThresholds.filter(th => th.reversed).length

  return (
    <div className="px-3 py-3">
      {/* Summary */}
      <div
        className="flex items-center justify-between px-3 py-2 mb-3 rounded-lg"
        style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="text-[11px] font-bold" style={{ color: 'rgba(180,220,190,0.6)' }}>
          {t('runInfo.clauseProgress', 'Progress')}
        </div>
        <div className="text-[13px] font-bold font-mono" style={{ color: '#C9A96E' }}>
          {reversedCount}/{clauseThresholds.length}
        </div>
      </div>

      {/* Clause list */}
      <div className="flex flex-col gap-2">
        {clauseThresholds.map((th) => {
          const clause = clausePool.find(c => c.id === th.clauseId)
          if (!clause) return null

          const isReversed = th.reversed
          const isInCurrentBlind = currentBlindClauseIds.includes(th.clauseId)
          const progress = isReversed ? 100 : isInCurrentBlind ? Math.min(100, (runningScore / th.threshold) * 100) : 0

          return (
            <div
              key={th.clauseId}
              className="rounded-lg overflow-hidden"
              style={{
                background: isReversed
                  ? 'rgba(125,175,139,0.12)'
                  : 'rgba(0,0,0,0.2)',
                border: isReversed
                  ? '1px solid rgba(125,175,139,0.3)'
                  : '1px solid rgba(255,255,255,0.06)',
              }}
            >
              {/* Header row */}
              <div className="flex items-center justify-between px-3 pt-2">
                <div className="flex items-center gap-1.5">
                  <span
                    className="text-[10px] font-mono font-bold"
                    style={{ color: isReversed ? '#7DAF8B' : 'rgba(180,220,190,0.6)' }}
                  >
                    {th.clauseId}
                  </span>
                  {clause.isBoss && (
                    <span
                      className="px-1 rounded-[2px] text-[7px] font-bold"
                      style={{ background: 'rgba(212,132,154,0.3)', color: '#D4849A' }}
                    >
                      BOSS
                    </span>
                  )}
                  {isReversed && (
                    <span
                      className="px-1.5 rounded-[3px] text-[8px] font-bold"
                      style={{ background: 'rgba(125,175,139,0.25)', color: '#7DAF8B' }}
                    >
                      {t('runInfo.reversed', 'Reversed')}
                    </span>
                  )}
                </div>
                <span
                  className="text-[10px] font-mono font-bold"
                  style={{ color: isReversed ? '#7DAF8B' : 'rgba(201,169,110,0.6)' }}
                >
                  {th.threshold.toLocaleString()}
                </span>
              </div>

              {/* Clause text */}
              <div
                className="px-3 pt-1 text-[11px] leading-snug"
                style={{ color: isReversed ? 'rgba(125,175,139,0.7)' : 'rgba(230,220,200,0.6)' }}
              >
                {clause.shortText}
              </div>

              {/* Progress bar */}
              <div className="mx-3 mt-2 mb-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.3)' }}>
                <div
                  className="h-full rounded-full transition-[width] duration-500"
                  style={{
                    width: `${progress}%`,
                    background: isReversed ? '#7DAF8B' : 'linear-gradient(90deg, #7CAFC2, #C9A96E)',
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// =============================================================================
// PanelHeader — reused panel header strip
// =============================================================================

function PanelHeader({ label }: { label: string }) {
  return (
    <div
      className="rounded-t-lg px-3 py-1.5 text-[10px] font-bold uppercase tracking-[2px] text-center"
      style={{
        background: 'linear-gradient(135deg, #4A3520, #5A4030, #4A3520)',
        color: '#C9A96E',
        border: '1px solid rgba(201,169,110,0.2)',
        borderBottom: 'none',
      }}
    >
      {label}
    </div>
  )
}
